import {
  NavLink,
  Outlet,
  useLoaderData,
  useRouteLoaderData,
} from "react-router-dom";
import CommonHeader from "../components/CommonHeader";
import CommonFooter from "../components/CommonFooter";
import { User } from "../services/user/userModel";
import {
  isFreeOrganizationAccount,
  Organization,
} from "../services/organization/organizationModel";
import PrivateSidebar from "../components/PrivateSidebar";
import { useTranslation } from "react-i18next";
import { useCallback, useMemo, useState } from "react";
import { cx } from "../react-helpers/css";
import { getPlanLabelKey } from "../services/subscription/subscriptionModel";
import { PREMIUM_SPONSORSHIP_STATS_LINK } from "../services/config";

const OrganizationArea = () => {
  const { loggedUser } = useRouteLoaderData("logged-user") as {
    loggedUser?: User;
  };
  const { organization } = useLoaderData() as { organization: Organization };
  const { t } = useTranslation(["common", "organization", "subscription"]);
  const [displayMobileMenu, setDisplayMobileMenu] = useState(false);

  const onClose = useCallback(() => setDisplayMobileMenu(false), []);

  // Free pro account: the sidebar only keeps the "my candidates" and
  // "my account" links, and the header drops the recruiter guide.
  const isFreeAccount = useMemo(
    () => isFreeOrganizationAccount(organization, loggedUser),
    [loggedUser, organization],
  );

  // The backend only returns subscriptions that have not expired yet, so an
  // entry on a Pro+ plan means the section is currently active.
  const hasActiveProPlus = useMemo(
    () =>
      (organization.subscriptions ?? []).some(
        (subscription) => getPlanLabelKey(subscription.plan.type) === "support",
      ),
    [organization.subscriptions],
  );

  return (
    <>
      <CommonHeader
        loggedUser={loggedUser}
        setDisplayMobileMenu={setDisplayMobileMenu}
        hideRecruiterGuide={isFreeAccount}
      />

      <div
        className={cx(["app-layout --h", displayMobileMenu && "--menu-opened"])}
      >
        {loggedUser && (
          <PrivateSidebar name={organization.legalName} onCloseMenu={onClose}>
            <nav className="container--s">
              {/* <div className="cblock">
                <NavLink
                  to="/organization"
                  end
                  className="link"
                  onClick={onClose}
                >
                  {t("common:sidebar.HOME")}
                </NavLink>
              </div> */}

              <div className="lblock">
                <NavLink
                  to="/organization/candidates"
                  className="btn --block"
                  onClick={onClose}
                >
                  {t("common:header.MY_CANDIDATES")}
                </NavLink>
              </div>
              <div className="cblock--s">
                <NavLink
                  to="/organization/invited-candidates"
                  className="btn--2 --outlined --block"
                  onClick={onClose}
                >
                  {t("common:header.INVITED_CANDIDATES")}
                </NavLink>
              </div>
              {!isFreeAccount && (
                <div className="cblock--s">
                  <NavLink
                    to="/organization/capsules"
                    className="btn--2 --block"
                    onClick={onClose}
                  >
                    {t("common:header.MY_LISTINGS")}
                  </NavLink>
                </div>
              )}
              {loggedUser.adminOrga && !isFreeAccount && (
                <div className="lblock">
                  <div className="cblock">
                    <strong>{t("organization:ADMINISTRATION")}</strong>
                  </div>
                  <div className="container--s cblock--s --bordered-left sidebar_subnav">
                    <div>
                      <NavLink
                        to="/organization/pro/users"
                        className="link --s"
                        onClick={onClose}
                      >
                        {t("organization:users.PAGE_TITLE")}
                      </NavLink>
                    </div>
                    <div className="cblock--xs">
                      <NavLink
                        to="/organization/pro/subscription"
                        className="link --s"
                        onClick={onClose}
                      >
                        {t("organization:subscription.PAGE_TITLE")}
                      </NavLink>
                    </div>
                    <div className="cblock--xs">
                      <NavLink
                        to="/organization/pro/account"
                        className="link --s"
                        onClick={onClose}
                      >
                        {t("organization:pro-account.PAGE_TITLE")}
                      </NavLink>
                    </div>
                  </div>
                </div>
              )}
              {loggedUser.adminOrga && hasActiveProPlus && (
                <div className="lblock">
                  <div className="cblock sidebar_group_head">
                    <strong>{t("subscription:label.support")}</strong>
                    <span className="sidebar_badge">
                      {t("organization:PRO_PLUS_ACTIVE")}
                    </span>
                  </div>
                  <div className="container--s cblock--s --bordered-left sidebar_subnav">
                    <div>
                      <NavLink
                        to="/organization/pro/premium-sponsorship"
                        className="link --s"
                        onClick={onClose}
                      >
                        {t("organization:premium-sponsorship.PAGE_TITLE")}
                      </NavLink>
                    </div>
                    <div className="cblock--xs">
                      <a
                        href={PREMIUM_SPONSORSHIP_STATS_LINK}
                        className="link --s --external"
                        target="_blank"
                        rel="noreferrer"
                        onClick={onClose}
                      >
                        {t("organization:premium-sponsorship.STATS_BUTTON")}
                      </a>
                    </div>
                  </div>
                </div>
              )}
              <div className="lblock">
                <NavLink
                  className="link"
                  to={"/organization/account"}
                  onClick={onClose}
                >
                  {t("common:header.MY_ACCOUNT")}
                </NavLink>
              </div>
            </nav>
          </PrivateSidebar>
        )}
        <div className={"layout_content --bg--organization-dashboard"}>
          <Outlet />
          <CommonFooter loggedUser={loggedUser} />
        </div>
      </div>
    </>
  );
};

export default OrganizationArea;
